import { Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { renderLanding } from '../../feature-flags';

export type DiscoverProduct = 'references' | 'render';
// The bar is white-on-dark everywhere except the render pages, which are light (the boss's
// render prototype is a light design).
export type DiscoverTopbarTheme = 'dark' | 'light';

// Google Fonts request for the landing typeface. Injected on demand instead of living in
// index-web/index.html because the pages using it are flag-gated (dev only).
const MANROPE_LINK_ID = 'discover-landing-manrope';
const MANROPE_HREF = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400..800&display=swap';

// The brand lock from the designer's Figma frame: one "3DWay" mark, then "references | render"
// as plain text (2026-09-18: the mark keeps its tight tracking, the product words do not) +
// Account. The words switch between the two products' landings: references → /discover,
// render → /render where that page exists (renderLanding flag), else /library (the classic
// "Stop Rendering" landing for guests, the library when signed in); the mark goes to the
// current product's landing. The host page decides what Account does.
@Component({
  selector: 'app-discover-topbar',
  templateUrl: './discover-topbar.component.html',
  styleUrls: ['./discover-topbar.component.scss'],
})
export class DiscoverTopbarComponent implements OnInit {
  @Input() active: DiscoverProduct = 'references';
  @Input() theme: DiscoverTopbarTheme = 'dark';
  @Output() accountClicked = new EventEmitter<void>();

  public readonly products: ReadonlyArray<{ id: DiscoverProduct; label: string; link: string }> = [
    { id: 'references', label: 'references', link: '/discover' },
    { id: 'render', label: 'render', link: renderLanding ? '/render' : '/library' },
  ];

  @HostBinding('class.theme-light')
  public get isLight(): boolean {
    return this.theme === 'light';
  }

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public get activeLink(): string {
    return this.products.find((product) => product.id === this.active)?.link || this.products[0].link;
  }

  ngOnInit(): void {
    this.ensureManropeLoaded();
  }

  public onAccountClick(): void {
    this.accountClicked.emit();
  }

  private ensureManropeLoaded(): void {
    if (this.document.getElementById(MANROPE_LINK_ID)) {
      return;
    }

    const link = this.document.createElement('link');
    link.id = MANROPE_LINK_ID;
    link.rel = 'stylesheet';
    link.href = MANROPE_HREF;
    this.document.head.appendChild(link);
  }
}
